// The one list of collection names for web, mobile and backend; `firestore.rules` cannot import it and repeats them.

/** Read or written by the apps, and usually by the backend too. */
export const SharedCollections = {
  Users: 'users',
  LifeAreas: 'life-areas',
  Lifescans: 'lifescans',
  Tasks: 'tasks',
  Goals: 'goals',
  GoalTemplates: 'goal-templates',
  Tags: 'tags',
  Relationships: 'relationships',
  SmartLists: 'smart-lists',
  FcmTokens: 'fcm-tokens',
  ExternalConnections: 'external-connections',
  Visions: 'visions',
  // Written by the local-database migration and older clients; the rules give the owner access, no app reads it.
  Reflections: 'reflections',
} as const;

/** Only Cloud Functions touch these. */
export const BackendCollections = {
  ScheduledNotifications: 'scheduled-notifications',
  GoalTemplateCategories: 'goal-template-categories',
  LifeAreaTemplates: 'life-area-templates',
  Mutex: 'mutex',
  CalendarResyncs: 'calendar-resyncs',
  CalendarResyncRetries: 'calendar-resync-retries',
  CalendarOutboundPending: 'calendar-outbound-pending',
  FeaturebaseSsoRateLimits: 'featurebase-sso-rate-limits',
  RevenuecatCustomers: 'revenuecat-customers',
  RevenuecatEvents: 'revenuecat-events',
  PendingExternalAuth: 'pending-external-auth',
  SignupClaims: 'signup-claims',
  DesktopSignInCodes: 'desktop-sign-in-codes',
} as const;

export const FirestoreCollections = {
  ...SharedCollections,
  ...BackendCollections,
} as const;

export type FirestoreCollection =
  (typeof FirestoreCollections)[keyof typeof FirestoreCollections];

/** The names the apps may read and write. */
export type SharedCollection =
  (typeof SharedCollections)[keyof typeof SharedCollections];

/** Collections nested under a document, e.g. `external-connections/{id}/taskSyncs`. */
export const FirestoreSubcollections = {
  TaskSyncs: 'taskSyncs',
  OutboundQueue: 'outboundQueue',
  Runtime: 'runtime',
} as const;

// `tsc` fails when a key sits in two groups (the spread would silently overwrite it) or a name is written twice.
type Overlap<A, B> = keyof A & keyof B;
type DuplicateNames<T extends Record<string, string>> = {
  [K in keyof T]: {
    [J in Exclude<keyof T, K>]: T[J] extends T[K] ? J : never;
  }[Exclude<keyof T, K>];
}[keyof T];
type AssertNever<T extends never> = T;

type CollectionGroupsAreDisjoint = AssertNever<
  Overlap<typeof SharedCollections, typeof BackendCollections>
>;
type CollectionNamesAreUnique = AssertNever<
  DuplicateNames<typeof FirestoreCollections & typeof FirestoreSubcollections>
>;
